import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  EnvelopeIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
  FunnelIcon,
  InboxIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  LifebuoyIcon
} from '@heroicons/react/24/outline';

const statusConfig = {
  new: { label: 'New', color: 'bg-blue-500/20 text-blue-400', icon: ClockIcon },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/20 text-amber-400', icon: ArrowPathIcon },
  replied: { label: 'Replied', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircleIcon },
  closed: { label: 'Closed', color: 'bg-surface-500/20 text-surface-400', icon: XCircleIcon }
};

const sourceConfig = {
  contact_form: { label: 'Contact Form', color: 'bg-purple-500/20 text-purple-400', icon: DocumentTextIcon },
  email_inbound: { label: 'Email', color: 'bg-cyan-500/20 text-cyan-400', icon: EnvelopeIcon }
};

const mailboxConfig = {
  sales: { 
    label: 'Sales', 
    icon: CurrencyDollarIcon, 
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
    email: 'sales@bi.neoshift.ai',
    description: 'Sales inquiries and contact form submissions'
  },
  support: { 
    label: 'Support', 
    icon: LifebuoyIcon, 
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    email: 'support@bi.neoshift.ai',
    description: 'Customer support requests and technical issues'
  }
};

function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatRelativeTime(date) {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(date);
}

// ==================== CONTACT DETAIL MODAL ====================
function ContactDetailModal({ isOpen, onClose, contactId, onUpdate }) {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [admins, setAdmins] = useState([]);

  useEffect(() => {
    if (isOpen && contactId) {
      loadContact();
      loadAdmins();
    }
  }, [isOpen, contactId]);

  const loadContact = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getMailboxContact(contactId);
      setContact(response.data.contact);
    } catch (error) {
      toast.error('Failed to load contact');
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    try {
      const response = await adminApi.getMailboxAdmins();
      setAdmins(response.data.admins);
    } catch (error) {
      console.error('Failed to load admins:', error);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await adminApi.replyToMailboxContact(contactId, newMessage);
      setNewMessage('');
      toast.success('Reply sent successfully');
      loadContact();
      onUpdate();
    } catch (error) {
      const errorMsg = error.response?.data?.warning || error.response?.data?.error || 'Failed to send reply';
      if (error.response?.data?.warning) {
        toast.success('Reply saved');
        toast.error(errorMsg);
        loadContact();
        onUpdate();
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await adminApi.updateMailboxContact(contactId, { status });
      toast.success('Status updated');
      loadContact();
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssign = async (adminId) => {
    try {
      await adminApi.updateMailboxContact(contactId, { assignedTo: adminId || null });
      toast.success('Assignment updated');
      loadContact();
      onUpdate();
    } catch (error) {
      toast.error('Failed to update assignment');
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-end">
        <Dialog.Panel className="h-full w-full max-w-2xl bg-surface-900 border-l border-surface-700 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-700">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              Sales Contact Details
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : contact ? (
            <>
              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {/* Contact Info */}
                <div className="p-6 border-b border-surface-700">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                      <EnvelopeIcon className="w-6 h-6 text-primary-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl font-semibold text-surface-100">{contact.subject}</h2>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${sourceConfig[contact.source]?.color}`}>
                          {sourceConfig[contact.source]?.label}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1 text-sm">
                        {contact.name && (
                          <div className="flex items-center gap-2 text-surface-300">
                            <UserIcon className="w-4 h-4 text-surface-500" />
                            {contact.name}
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-surface-300">
                          <EnvelopeIcon className="w-4 h-4 text-surface-500" />
                          <span className="text-surface-400 mr-1">From:</span>
                          <a href={`mailto:${contact.email}`} className="text-primary-400 hover:underline">
                            {contact.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-surface-300">
                          <EnvelopeIcon className="w-4 h-4 text-surface-500" />
                          <span className="text-surface-400 mr-1">To:</span>
                          <span className="text-surface-300">
                            {contact.mailbox === 'sales' ? 'sales@bi.neoshift.ai' : 'support@bi.neoshift.ai'}
                          </span>
                        </div>
                        <div className="text-surface-500 text-xs mt-2">
                          Ref: {contact.contactNumber || `SC-${contact._id?.slice(-6).toUpperCase()}`}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Initial Message */}
                  <div className="mt-4 bg-surface-800 rounded-lg p-4">
                    <div className="text-sm text-surface-400 mb-2">Initial Message</div>
                    <p className="text-surface-200 whitespace-pre-wrap">{contact.initialMessage}</p>
                    <div className="text-xs text-surface-500 mt-2">
                      {formatDate(contact.createdAt)}
                    </div>
                  </div>

                  {/* Status & Assignment */}
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-surface-400 mb-1">Status</label>
                      <select
                        value={contact.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="input"
                      >
                        <option value="new">New</option>
                        <option value="in_progress">In Progress</option>
                        <option value="replied">Replied</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-surface-400 mb-1">Assigned To</label>
                      <select
                        value={contact.assignedTo?._id || ''}
                        onChange={(e) => handleAssign(e.target.value)}
                        className="input"
                      >
                        <option value="">Unassigned</option>
                        {admins.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.firstName} {admin.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Messages Thread */}
                <div className="p-6">
                  <h3 className="text-sm font-medium text-surface-400 mb-4">
                    <ChatBubbleLeftRightIcon className="w-4 h-4 inline mr-2" />
                    Conversation ({contact.messages?.length || 0} replies)
                  </h3>

                  <div className="space-y-4">
                    {contact.messages?.map((message, index) => (
                      <div
                        key={message._id || index}
                        className={`flex ${message.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            message.sender === 'admin'
                              ? 'bg-primary-500/20 text-primary-100'
                              : 'bg-surface-700 text-surface-200'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{message.content}</p>
                          <div className="text-xs mt-2 opacity-60">
                            {message.sender === 'admin' && message.sentBy && (
                              <span>{message.sentBy.firstName} {message.sentBy.lastName} • </span>
                            )}
                            {message.sender === 'customer' && (
                              <span>{contact.name || contact.email} • </span>
                            )}
                            {formatDate(message.sentAt)}
                          </div>
                        </div>
                      </div>
                    ))}

                    {(!contact.messages || contact.messages.length === 0) && (
                      <div className="text-center text-surface-500 py-8">
                        No replies yet. Send a response below.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Reply Form */}
              {contact.status !== 'closed' && (
                <form onSubmit={handleSendReply} className="p-4 border-t border-surface-700">
                  <div className="mb-2">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your reply... (This will be sent to the customer's email)"
                      className="input w-full min-h-[100px]"
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-surface-500">
                      Reply will be sent to: {contact.email}
                    </p>
                    <button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="btn-primary"
                    >
                      {sending ? (
                        <>
                          <ArrowPathIcon className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <PaperAirplaneIcon className="w-5 h-5" />
                          Send Reply
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {contact.status === 'closed' && (
                <div className="p-4 border-t border-surface-700 bg-surface-800/50 text-center text-surface-400">
                  This conversation is closed. Change status to reopen.
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-surface-400">
              Contact not found
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// ==================== MAIN COMPONENT ====================
export default function Mailbox() {
  const { mailboxType = 'sales' } = useParams();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [allStats, setAllStats] = useState({ sales: {}, support: {} });
  const [selectedContact, setSelectedContact] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const currentMailbox = mailboxConfig[mailboxType] || mailboxConfig.sales;

  useEffect(() => {
    loadContacts();
    loadStats();
    loadAllStats();
  }, [mailboxType, pagination.page, filters]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getMailboxContacts(mailboxType, {
        page: pagination.page,
        limit: 20,
        status: filters.status || undefined,
        source: filters.source || undefined,
        search: filters.search || undefined
      });
      setContacts(response.data.contacts || []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminApi.getMailboxStats(mailboxType);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadAllStats = async () => {
    try {
      const [salesRes, supportRes] = await Promise.all([
        adminApi.getMailboxStats('sales'),
        adminApi.getMailboxStats('support')
      ]);
      setAllStats({
        sales: salesRes.data,
        support: supportRes.data
      });
    } catch (error) {
      console.error('Failed to load all stats:', error);
    }
  };

  const handleUpdate = () => {
    loadContacts();
    loadStats();
    loadAllStats();
  };

  const getStatusCount = (status) => {
    const statusData = stats.byStatus?.find(s => s._id === status);
    return statusData?.count || 0;
  };

  const getSourceCount = (source) => {
    const sourceData = stats.bySource?.find(s => s._id === source);
    return sourceData?.count || 0;
  };

  const totalCount = stats.byStatus?.reduce((sum, s) => sum + s.count, 0) || 0;

  const handleMailboxChange = (newMailbox) => {
    navigate(`/mailbox/${newMailbox}`);
    setPagination({ page: 1, pages: 1, total: 0 });
    setFilters({ status: '', source: '', search: '' });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with Mailbox Tabs */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Mailbox</h1>
          <p className="text-surface-400 mt-1">Manage contact form submissions and inbound emails</p>
        </div>
        <div className="flex items-center gap-3">
          {stats.unreadCount > 0 && (
            <div className="bg-blue-500/20 text-blue-400 px-4 py-2 rounded-lg flex items-center gap-2">
              <InboxIcon className="w-5 h-5" />
              <span className="font-bold">{stats.unreadCount}</span>
              <span>unread</span>
            </div>
          )}
          {stats.newCount > 0 && (
            <div className="bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-lg flex items-center gap-2">
              <span className="font-bold">{stats.newCount}</span>
              <span>new</span>
            </div>
          )}
        </div>
      </div>

      {/* Mailbox Type Tabs */}
      <div className="flex gap-2">
        {Object.entries(mailboxConfig).map(([key, config]) => {
          const MailboxIcon = config.icon;
          const unreadCount = allStats[key]?.unreadCount || 0;
          const isActive = mailboxType === key;
          
          return (
            <button
              key={key}
              onClick={() => handleMailboxChange(key)}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                isActive
                  ? `${config.bgColor} ${config.color} ring-2 ring-${config.color.split('-')[1]}-500/30`
                  : 'bg-surface-800 text-surface-400 hover:text-surface-100 hover:bg-surface-700'
              }`}
            >
              <MailboxIcon className="w-5 h-5" />
              <span>{config.label}</span>
              {unreadCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  isActive ? 'bg-white/20' : 'bg-blue-500 text-white'
                }`}>
                  {unreadCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Current Mailbox Info */}
      <div className={`${currentMailbox.bgColor} rounded-lg p-4 flex items-center gap-3`}>
        <currentMailbox.icon className={`w-6 h-6 ${currentMailbox.color}`} />
        <div>
          <h3 className={`font-medium ${currentMailbox.color}`}>{currentMailbox.label} Mailbox</h3>
          <p className="text-sm text-surface-400">{currentMailbox.description} • {currentMailbox.email}</p>
        </div>
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilters({ ...filters, status: '' })}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            filters.status === ''
              ? 'bg-primary-500 text-white'
              : 'bg-surface-800 text-surface-400 hover:text-surface-100'
          }`}
        >
          All ({totalCount})
        </button>
        {Object.entries(statusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setFilters({ ...filters, status })}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              filters.status === status
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-400 hover:text-surface-100'
            }`}
          >
            <config.icon className="w-4 h-4" />
            {config.label} ({getStatusCount(status)})
          </button>
        ))}
      </div>

      {/* Source Filters & Search */}
      <div className="card">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <GlobeAltIcon className="w-5 h-5 text-surface-500" />
            <span className="text-sm text-surface-400">Source:</span>
            <select
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              className="input py-1.5 text-sm"
            >
              <option value="">All Sources</option>
              <option value="contact_form">Contact Form ({getSourceCount('contact_form')})</option>
              <option value="email_inbound">Inbound Email ({getSourceCount('email_inbound')})</option>
            </select>
          </div>
          <div className="flex-1 flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-surface-500" />
            <input
              type="text"
              placeholder="Search by email, name, or subject..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="input flex-1"
            />
          </div>
        </div>
      </div>

      {/* Contacts List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="card text-center py-12">
          <currentMailbox.icon className={`w-12 h-12 ${currentMailbox.color} mx-auto mb-4 opacity-50`} />
          <h3 className="text-lg font-medium text-surface-300 mb-2">No {currentMailbox.label.toLowerCase()} contacts found</h3>
          <p className="text-surface-400">
            Contacts will appear here when customers submit the contact form or email {currentMailbox.email}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact) => {
            const StatusIcon = statusConfig[contact.status]?.icon || ClockIcon;
            const SourceIcon = sourceConfig[contact.source]?.icon || EnvelopeIcon;
            
            return (
              <div
                key={contact._id}
                onClick={() => setSelectedContact(contact._id)}
                className={`card hover:border-surface-600 cursor-pointer transition-all ${
                  contact.unreadByAdmin ? 'border-l-4 border-l-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      contact.unreadByAdmin ? 'bg-blue-500/20' : 'bg-surface-700'
                    }`}>
                      <EnvelopeIcon className={`w-5 h-5 ${contact.unreadByAdmin ? 'text-blue-400' : 'text-surface-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-semibold truncate ${contact.unreadByAdmin ? 'text-surface-100' : 'text-surface-300'}`}>
                          {contact.subject}
                        </h3>
                        {contact.unreadByAdmin && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-surface-400">
                        {contact.name && (
                          <span className="flex items-center gap-1">
                            <UserIcon className="w-3 h-3" />
                            {contact.name}
                          </span>
                        )}
                        <span className="flex items-center gap-1 truncate">
                          <EnvelopeIcon className="w-3 h-3" />
                          {contact.email}
                        </span>
                      </div>
                      <p className="text-sm text-surface-500 mt-2 line-clamp-1">
                        {contact.threadPreview || contact.initialMessage?.substring(0, 100)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${sourceConfig[contact.source]?.color}`}>
                        <SourceIcon className="w-3 h-3" />
                        {sourceConfig[contact.source]?.label}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${statusConfig[contact.status]?.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig[contact.status]?.label}
                      </span>
                    </div>
                    <span className="text-xs text-surface-500">
                      {formatRelativeTime(contact.lastMessageAt || contact.createdAt)}
                    </span>
                    {contact.messageCount > 1 && (
                      <span className="text-xs text-surface-400">
                        {contact.messageCount} messages
                      </span>
                    )}
                    {contact.assignedTo && (
                      <span className="text-xs text-surface-400">
                        → {contact.assignedTo.firstName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-surface-400 text-sm">
            Showing {contacts.length} of {pagination.total} contacts
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-surface-400 text-sm">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <ContactDetailModal
        isOpen={!!selectedContact}
        onClose={() => setSelectedContact(null)}
        contactId={selectedContact}
        onUpdate={handleUpdate}
      />
    </div>
  );
}
